import { parseNumber } from "../../../../shared/utils/number-utils.service";
import {
  CaPeriodeBucket,
  CaPeriodeScale,
  periodKey,
  periodLabel,
} from "./ca-par-periode-tiers.component";

export interface ExcelLotRow {
  lotId: string | null;
  lotNumero: string;
  qty: number; // Qté en UV (vendue)
  qtyUG: number; // Qté en UG (= qty × coefUG)
  coefUG: number; // UG par UV
  prixUnitaire: number; // PU vente (€/UV)
  puCoutMat: number; // PU coût matière profond (€/UG)
  puCoutMO: number; // PU coût MO profond (€/UG)
  ca: number;
  costMat: number;
  costMO: number;
}

export interface ExcelArticleRow {
  articleId: string;
  articleName: string;
  ca: number;
  costMat: number;
  costMO: number;
  lots: ExcelLotRow[];
}

export interface ExcelPeriodRow {
  d: Date;
  label: string;
  ca: number;
  costMat: number;
  costMO: number;
  articles: ExcelArticleRow[];
}

/** Calcule les buckets agrégés simples (widget + dialog en mode chart). */
export function computeBucketsForScale(
  facturesLignes: any[],
  lotCostMap: Map<string, { mat: number; mo: number }>,
  scale: CaPeriodeScale,
  articleFilterId: string | null = null,
): CaPeriodeBucket[] {
  const byKey = new Map<
    string,
    { d: Date; label: string; ca: number; costMat: number; costMO: number }
  >();
  let minD: Date | null = null;
  let maxD: Date | null = null;

  for (const fl of facturesLignes) {
    const f = fl?.facture ?? fl?.f;
    const dRaw = f?.date ?? f?.dateString ?? fl?.createdAt;
    if (!dRaw) continue;
    const d = new Date(dRaw);
    if (isNaN(d.getTime())) continue;

    const articleId: string =
      fl?.commandeClientLigne?.articleId ??
      fl?.articleId ??
      fl?.article?.id ??
      "inconnu";
    if (articleFilterId && String(articleId) !== String(articleFilterId)) continue;

    const coefUG =
      parseNumber(fl?.commandeClientLigne?.coefficientUniteGestion ?? 1) || 1;
    const qtyUV = parseNumber(fl?.quantite ?? 0);
    const qtyUG = qtyUV * coefUG;
    const puVenteUV = parseNumber(fl?.prix ?? 0);
    const ca = qtyUV * puVenteUV;

    const lotId: string | undefined = fl?.lotId ?? fl?.lot?.id;
    const lotCosts = lotId ? lotCostMap.get(lotId) : null;
    const costMat = qtyUG * (lotCosts?.mat ?? 0);
    const costMO = qtyUG * (lotCosts?.mo ?? 0);

    const pk = periodKey(d, scale);
    if (!minD || d < minD) minD = d;
    if (!maxD || d > maxD) maxD = d;

    const prev = byKey.get(pk);
    if (prev) {
      prev.ca += ca;
      prev.costMat += costMat;
      prev.costMO += costMO;
    } else {
      byKey.set(pk, { d, label: periodLabel(d, scale), ca, costMat, costMO });
    }
  }

  if (!byKey.size) return [];

  return [...byKey.entries()]
    .map(([, b]) => {
      const cost = b.costMat + b.costMO;
      const margin = b.ca - cost;
      const marginPct = b.ca !== 0 ? margin / b.ca : 0;
      return {
        label: b.label,
        d: b.d,
        ca: b.ca,
        costMat: b.costMat,
        costMO: b.costMO,
        cost,
        margin,
        marginPct,
      };
    })
    .sort((a, b) => a.d.getTime() - b.d.getTime());
}

/**
 * Calcule l'arbre complet Période → Article → Lot pour l'export Excel.
 * Les lignes sont triées chronologiquement (période) puis par CA desc
 * (article et lot).
 */
export function computeTreeForScale(
  facturesLignes: any[],
  lotCostMap: Map<string, { mat: number; mo: number }>,
  scale: CaPeriodeScale,
  articleFilterId: string | null,
  articleNameResolver: (articleId: string) => string,
): ExcelPeriodRow[] {
  const periodMap = new Map<
    string,
    {
      d: Date;
      label: string;
      ca: number;
      costMat: number;
      costMO: number;
      articles: Map<
        string,
        {
          articleId: string;
          ca: number;
          costMat: number;
          costMO: number;
          lots: Map<
            string,
            {
              lotId: string | null;
              lotNumero: string;
              qty: number;
              qtyUG: number;
              ca: number;
              costMat: number;
              costMO: number;
              prixCumul: number;
              prixWeight: number;
              coefUG: number;
              puCoutMat: number;
              puCoutMO: number;
            }
          >;
        }
      >;
    }
  >();

  for (const fl of facturesLignes) {
    const f = fl?.facture ?? fl?.f;
    const dRaw = f?.date ?? f?.dateString ?? fl?.createdAt;
    if (!dRaw) continue;
    const d = new Date(dRaw);
    if (isNaN(d.getTime())) continue;

    const articleId: string =
      fl?.commandeClientLigne?.articleId ??
      fl?.articleId ??
      fl?.article?.id ??
      "inconnu";
    if (articleFilterId && String(articleId) !== String(articleFilterId)) continue;

    const coefUG =
      parseNumber(fl?.commandeClientLigne?.coefficientUniteGestion ?? 1) || 1;
    const qtyUV = parseNumber(fl?.quantite ?? 0);
    const qtyUG = qtyUV * coefUG;
    const puVenteUV = parseNumber(fl?.prix ?? 0);
    const ca = qtyUV * puVenteUV;

    const lotId: string | null = fl?.lotId ?? fl?.lot?.id ?? null;
    const lotNumero: string = fl?.lot?.numero ?? "—";
    const lotCosts = lotId ? lotCostMap.get(lotId) : null;
    const costMat = qtyUG * (lotCosts?.mat ?? 0);
    const costMO = qtyUG * (lotCosts?.mo ?? 0);

    const pk = periodKey(d, scale);
    if (!periodMap.has(pk)) {
      periodMap.set(pk, {
        d,
        label: periodLabel(d, scale),
        ca: 0,
        costMat: 0,
        costMO: 0,
        articles: new Map(),
      });
    }
    const per = periodMap.get(pk)!;
    per.ca += ca;
    per.costMat += costMat;
    per.costMO += costMO;

    if (!per.articles.has(articleId)) {
      per.articles.set(articleId, {
        articleId,
        ca: 0,
        costMat: 0,
        costMO: 0,
        lots: new Map(),
      });
    }
    const art = per.articles.get(articleId)!;
    art.ca += ca;
    art.costMat += costMat;
    art.costMO += costMO;

    const lotKey = lotId ?? `_nolot_${lotNumero}`;
    if (!art.lots.has(lotKey)) {
      art.lots.set(lotKey, {
        lotId,
        lotNumero,
        qty: 0,
        qtyUG: 0,
        ca: 0,
        costMat: 0,
        costMO: 0,
        prixCumul: 0,
        prixWeight: 0,
        coefUG,
        puCoutMat: lotCosts?.mat ?? 0,
        puCoutMO: lotCosts?.mo ?? 0,
      });
    }
    const lot = art.lots.get(lotKey)!;
    lot.qty += qtyUV;
    lot.qtyUG += qtyUG;
    lot.ca += ca;
    lot.costMat += costMat;
    lot.costMO += costMO;
    lot.prixCumul += puVenteUV * qtyUV;
    lot.prixWeight += qtyUV;
    // `coefUG` et `puCout*` sont stables par (article, lot), on garde la
    // première valeur rencontrée (cas de lignes multiples, remise inchangée).
  }

  const periods: ExcelPeriodRow[] = [];
  for (const per of periodMap.values()) {
    const articles: ExcelArticleRow[] = [];
    for (const art of per.articles.values()) {
      const lots: ExcelLotRow[] = [...art.lots.values()]
        .map((l) => ({
          lotId: l.lotId,
          lotNumero: l.lotNumero,
          qty: l.qty,
          qtyUG: l.qtyUG,
          coefUG: l.coefUG,
          prixUnitaire: l.prixWeight > 0 ? l.prixCumul / l.prixWeight : 0,
          puCoutMat: l.puCoutMat,
          puCoutMO: l.puCoutMO,
          ca: l.ca,
          costMat: l.costMat,
          costMO: l.costMO,
        }))
        .sort((a, b) => b.ca - a.ca);
      articles.push({
        articleId: art.articleId,
        articleName: articleNameResolver(art.articleId),
        ca: art.ca,
        costMat: art.costMat,
        costMO: art.costMO,
        lots,
      });
    }
    articles.sort((a, b) => b.ca - a.ca);
    periods.push({
      d: per.d,
      label: per.label,
      ca: per.ca,
      costMat: per.costMat,
      costMO: per.costMO,
      articles,
    });
  }
  periods.sort((a, b) => a.d.getTime() - b.d.getTime());
  return periods;
}
